import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { notify } from '../../helper/notify';
import {
    getInvoiceSendSummary,
    sendInvoiceToCustomer,
    InvoiceSendSummary,
} from '../../api/invoice-send';
import '../../scss/send-invoice-to-customer-modal.style.scss';

interface SendInvoiceToCustomerModalProps {
    show: boolean;
    invoice: any;
    onClose: () => void;
    /** Fired after a successful send so the caller can refresh the list. */
    onSuccess?: () => void;
}

/** "2026-09-10 14:32" - the summary needs the time of day, which formatDate drops. */
const formatSentAt = (value?: string | null): string => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())} ${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
};

const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

/**
 * Two-step resend flow for an approved invoice, as specced by the client:
 * step 1 summarises when the invoice was last sent and to which addresses,
 * step 2 shows the address it will go to and lets the freelancer correct it.
 *
 * The first invoice email is always sent by admin - this is a resend only.
 * A corrected address is saved to the customer record by the API, so this is
 * also how a freelancer fixes a wrong customer email without an edit request.
 */
const SendInvoiceToCustomerModal: React.FC<SendInvoiceToCustomerModalProps> = ({
    show,
    invoice,
    onClose,
    onSuccess,
}) => {
    const { t } = useTranslation();
    const [step, setStep] = useState<'summary' | 'recipient'>('summary');
    const [summary, setSummary] = useState<InvoiceSendSummary | null>(null);
    const [toEmail, setToEmail] = useState('');
    const [emailError, setEmailError] = useState<string | null>(null);
    const [loadingSummary, setLoadingSummary] = useState(false);
    const [sending, setSending] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);

    // ==================== Effects ====================
    useEffect(() => {
        if (!show || !invoice?.invoiceNumber) return;

        setStep('summary');
        setSummary(null);
        setToEmail('');
        setEmailError(null);
        setLoadError(null);
        fetchSummary();
    }, [show, invoice?.invoiceNumber]);

    // ==================== API Calls ====================
    const fetchSummary = async () => {
        setLoadingSummary(true);
        try {
            const data = await getInvoiceSendSummary(invoice.invoiceNumber);
            setSummary(data);
            setToEmail(data?.recipientEmail || '');
        } catch (error: any) {
            console.error('Error loading invoice send summary:', error);
            setLoadError(
                error?.response?.data?.message ||
                t('AN_ERROR_OCCURRED_WHILE_LOADING_SEND_SUMMARY')
            );
        } finally {
            setLoadingSummary(false);
        }
    };

    const handleSend = async () => {
        const trimmed = toEmail.trim();
        if (!trimmed) {
            setEmailError(t('PLEASE_ENTER_AN_EMAIL_ADDRESS'));
            return;
        }
        if (!isValidEmail(trimmed)) {
            setEmailError(t('PLEASE_ENTER_A_VALID_EMAIL_ADDRESS'));
            return;
        }
        if (mustChangeEmail && trimmed.toLowerCase() === lastSentTo.toLowerCase()) {
            setEmailError(t('INVOICE_ALREADY_SENT_TO_THIS_ADDRESS', { email: lastSentTo }));
            return;
        }

        setEmailError(null);
        setSending(true);
        try {
            const result = await sendInvoiceToCustomer(invoice.invoiceNumber, trimmed);

            // The API reports a failed send inside a 200 envelope (statusCode + message in
            // the body), so a resolved promise is not proof the invoice went out. Without
            // this check a swallowed send failure shows a success toast while the send
            // history stays on the previous send.
            const sent =
                (result?.statusCode === 200 || result?.statusCode === 201) && result?.response === true;
            if (!sent) {
                notify.error(result?.message || t('AN_ERROR_OCCURRED_WHILE_SENDING_THE_INVOICE'));
                return;
            }

            notify.success(`${t('INVOICE_SENT_TO_CUSTOMER_SUCCESSFULLY')} #${invoice?.invoiceNumber}`);
            onClose();
            onSuccess?.();
        } catch (error: any) {
            console.error('Error sending invoice to customer:', error);
            notify.error(
                error?.response?.data?.message ||
                t('AN_ERROR_OCCURRED_WHILE_SENDING_THE_INVOICE')
            );
        } finally {
            setSending(false);
        }
    };

    // ==================== Handlers ====================
    const handleClose = () => {
        if (!sending) onClose();
    };

    const emailChanged =
        !!summary && toEmail.trim().toLowerCase() !== (summary.recipientEmail || '').trim().toLowerCase();

    /**
     * A resend is only accepted to a corrected address - the API rejects sending
     * again to the address the invoice already reached with a 409. Mirror that rule
     * here so the button explains itself instead of failing on submit.
     */
    const lastSentTo = (summary?.lastSentToEmail || summary?.lastSent?.toEmail || '').trim();
    const mustChangeEmail = !!summary?.requiresEmailChange && !!lastSentTo;
    const sendsToLastAddress =
        mustChangeEmail && toEmail.trim().toLowerCase() === lastSentTo.toLowerCase();

    // ==================== Renderers ====================
    const renderSummaryStep = () => {
        if (loadingSummary) {
            return <p className="send-invoice-modal__muted">{t('LOADING')}</p>;
        }
        if (loadError) {
            return <p className="send-invoice-modal__error-text">{loadError}</p>;
        }
        if (!summary) return null;

        return (
            <>
                {!summary.canSend && (
                    <div className="send-invoice-modal__notice send-invoice-modal__notice--blocked">
                        {summary.blockReason}
                    </div>
                )}

                {summary.lastSent ? (
                    <div className="send-invoice-modal__section">
                        <h3 className="send-invoice-modal__section-title">{t('LAST_SENT')}</h3>
                        <dl className="send-invoice-modal__facts">
                            <dt>{t('DATE')}</dt>
                            <dd>{formatSentAt(summary.lastSent.sentDate)}</dd>
                            <dt>{t('TO')}</dt>
                            <dd>{summary.lastSent.toEmail || '-'}</dd>
                            {!!summary.lastSent.ccEmail && (
                                <>
                                    <dt>{t('CC')}</dt>
                                    <dd>{summary.lastSent.ccEmail}</dd>
                                </>
                            )}
                            <dt>{t('SENT_BY')}</dt>
                            <dd>{summary.lastSent.sentByName || '-'}</dd>
                        </dl>

                        {summary.canSend && mustChangeEmail && (
                            <p className="send-invoice-modal__hint">
                                {t('RESEND_ONLY_TO_A_CORRECTED_ADDRESS')}
                            </p>
                        )}

                        {summary.sentHistory.length > 1 && (
                            <details className="send-invoice-modal__history">
                                <summary>
                                    {t('EARLIER_SENDS', { count: summary.sentHistory.length - 1 })}
                                </summary>
                                <ul>
                                    {summary.sentHistory.slice(1).map((item, index) => (
                                        <li key={`${item.sentDate}-${index}`}>
                                            <span>{formatSentAt(item.sentDate)}</span>
                                            <span>{item.toEmail || '-'}</span>
                                        </li>
                                    ))}
                                </ul>
                            </details>
                        )}
                    </div>
                ) : summary.canSend ? (
                    /* Defensive - a sendable invoice always has a previous send,
                       since the first email is an admin action. */
                    <div className="send-invoice-modal__notice">
                        {t('THIS_INVOICE_HAS_NOT_BEEN_SENT_YET')}
                    </div>
                ) : null}
            </>
        );
    };

    const renderRecipientStep = () => (
        <div className="send-invoice-modal__section">
            <label className="send-invoice-modal__label" htmlFor="send-invoice-to-email">
                {t('THE_INVOICE_WILL_BE_SENT_TO')}
            </label>
            <input
                id="send-invoice-to-email"
                type="email"
                className={`send-invoice-modal__input${emailError ? ' send-invoice-modal__input--error' : ''}`}
                value={toEmail}
                onChange={(e) => {
                    setToEmail(e.target.value);
                    if (emailError) setEmailError(null);
                }}
                disabled={sending}
                placeholder={t('CUSTOMER_EMAIL_ADDRESS')}
            />
            {emailError && <p className="send-invoice-modal__error-text">{emailError}</p>}

            {sendsToLastAddress && !emailError && (
                <p className="send-invoice-modal__hint">
                    {t('INVOICE_ALREADY_SENT_TO_THIS_ADDRESS', { email: lastSentTo })}
                </p>
            )}

            {emailChanged && (
                <p className="send-invoice-modal__hint">
                    {t('THIS_EMAIL_WILL_ALSO_BE_SAVED_ON_THE_CUSTOMER')}
                </p>
            )}

            {!!summary?.ccEmail?.length && (
                <dl className="send-invoice-modal__facts send-invoice-modal__facts--tight">
                    <dt>{t('CC')}</dt>
                    <dd>{summary.ccEmail.join(', ')}</dd>
                </dl>
            )}
        </div>
    );

    const renderFooter = () => {
        if (step === 'summary') {
            return (
                <div className="modal-footer mt-3">
                    <button type="button" className="btn btn-secondary" onClick={handleClose}>
                        {t('Cancel')}
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => setStep('recipient')}
                        disabled={loadingSummary || !summary?.canSend}
                    >
                        {t('PROCEED_TO_RESEND')}
                    </button>
                </div>
            );
        }

        return (
            <div className="modal-footer mt-3">
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setStep('summary')}
                    disabled={sending}
                >
                    {t('BACK')}
                </button>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSend}
                    disabled={sending || sendsToLastAddress}
                    title={sendsToLastAddress ? t('INVOICE_ALREADY_SENT_TO_THIS_ADDRESS', { email: lastSentTo }) : undefined}
                >
                    {sending ? (
                        <>
                            <span className="spinner"></span>
                            <span>{t('PROCESSING')}</span>
                        </>
                    ) : (
                        t('SEND_INVOICE')
                    )}
                </button>
            </div>
        );
    };

    // ==================== Early Return ====================
    if (!show) return null;

    // ==================== Render ====================
    // Portalled to document.body for the same reason RequestCreditInvoiceModal is:
    // the surrounding panel keeps a residual `transform`, which would otherwise
    // become the containing block for this fixed-position overlay.
    return ReactDOM.createPortal(
        <div
            className="send-invoice-modal-overlay"
            onClick={handleClose}
            role="presentation"
        >
            <div
                className="send-invoice-modal-container"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="send-invoice-modal-title"
            >
                <div className="modal-header pb-3">
                    <h2 className="modal-title" id="send-invoice-modal-title">
                        {t('SEND_INVOICE_TO_CUSTOMER')}
                        {invoice?.invoiceNumber ? ` #${invoice.invoiceNumber}` : ''}
                    </h2>
                    <button
                        className="modal-close-btn"
                        onClick={handleClose}
                        disabled={sending}
                        type="button"
                        aria-label="Close modal"
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path
                                d="M18 6L6 18M6 6L18 18"
                                stroke="#252525"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </button>
                </div>

                <div className="modal-body">
                    {step === 'summary' ? renderSummaryStep() : renderRecipientStep()}
                </div>

                {renderFooter()}
            </div>
        </div>,
        document.body
    );
};

export default SendInvoiceToCustomerModal;
